import React, { useState, FC, useMemo, useCallback } from 'react';
import { Checkbox, TextField, InputAdornment, IconButton } from '@mui/material';
import { Application } from 'reduxState/store/application/types';
import { selectedAppsType } from '../CampaignAppsSelector';
import { Clear } from '@mui/icons-material';
import AutoSizer from 'react-virtualized-auto-sizer';
import { FixedSizeList } from 'react-window';

/**
 * CampaignsAppList component for displaying and managing a list of applications in campaigns.
 *
 * This component provides a virtualized list interface for applications with search functionality,
 * bulk selection capabilities, and individual app selection. It's designed to handle large lists
 * of applications efficiently using react-window for virtualization.
 *
 * @component
 * @param {Object} props - The component props
 * @param {Application[]} props.apps - Array of applications to display in the list
 * @param {Function} props.setApps - Callback function to handle selected applications
 * @param {'Addable' | 'Selected'} props.title - Title indicating the type of list (apps that can be added or currently selected apps)
 *
 * @example
 * ```tsx
 * <CampaignsAppList
 *   apps={availableApps}
 *   setApps={handleAddApps}
 *   title="Addable"
 * />
 * ```
 *
 * @example
 * ```tsx
 * <CampaignsAppList
 *   apps={selectedApps}
 *   setApps={handleRemoveApps}
 *   title="Selected"
 * />
 * ```
 */

interface ICampaignAppList {
  apps: Application[];
  setApps: (apps: Application[]) => void;
  title: 'Addable' | 'Selected';
  disabled?: boolean;
}

const CampaignsAppList: FC<ICampaignAppList> = ({ apps, setApps, title, disabled }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedApps, setSelectedApps] = useState<selectedAppsType>(new Map());

  // Use useMemo to calculate filtered apps
  const filteredApps = useMemo(() => {
    if (!searchTerm.trim()) return apps;
    return apps.filter(
      app => app.name.toLowerCase().includes(searchTerm.toLowerCase()) || app.id.toString().includes(searchTerm),
    );
  }, [apps, searchTerm]);

  const allSelected = selectedApps.size !== 0 && selectedApps.size === filteredApps.length;

  const toggleSelectAllApps = useCallback(() => {
    if (allSelected) {
      setSelectedApps(new Map());
      return;
    }

    const newSelectedApps = new Map(selectedApps);

    // Only add apps that match the filter
    filteredApps.forEach(app => {
      newSelectedApps.set(app.id, app);
    });

    setSelectedApps(newSelectedApps);
  }, [allSelected, filteredApps, selectedApps]);

  const toggleSelectApp = useCallback((app: Application) => {
    setSelectedApps(prevSelected => {
      const newState = new Map(prevSelected);

      if (newState.has(app.id)) {
        newState.delete(app.id);
      } else {
        newState.set(app.id, app);
      }

      return newState;
    });
  }, []);

  const handleAppsButton = () => {
    setApps(Array.from(selectedApps.values()));
    setSelectedApps(new Map());
    setSearchTerm('');
  };

  const handleClearAppSelection = () => {
    setSelectedApps(new Map());
  };

  const handleClearSearchTerm = () => {
    setSearchTerm('');
  };

  const AppRow = useCallback(
    ({ index, style }: { index: number; style: React.CSSProperties }) => {
      const application = filteredApps[index];
      const isSelected = selectedApps.has(application.id);
      return (
        <div
          style={style}
          className={`flex items-center cursor-pointer hover:bg-gray-100 transition-colors ${
            isSelected ? 'bg-blue-50' : index % 2 === 0 ? 'bg-gray-50' : 'bg-white'
          }`}
          onClick={disabled ? undefined : () => toggleSelectApp(application)}
        >
          <Checkbox
            checked={isSelected}
            onChange={() => toggleSelectApp(application)}
            onClick={e => e.stopPropagation()}
            size="small"
            disabled={disabled}
          />
          <span className="text-xs text-gray-800" data-testid={`app-row-${application.id}`}>
            {application.id} - {application.name}
          </span>
        </div>
      );
    },
    [filteredApps, selectedApps, toggleSelectApp, disabled],
  );

  return (
    <div className="w-full min-w-0 flex-1">
      <div className="flex flex-wrap items-center justify-between gap-2 py-2 padding">
        <h4 className="text-muted-dark-purple">{title}</h4>
        <div className="flex gap-2">
          <button
            type="button"
            className={`px-4 py-2 text-gray-800 bg-gray-300 rounded ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            onClick={handleClearAppSelection}
            disabled={disabled}
          >
            Clear
          </button>
          <button
            type="button"
            className={`px-4 py-2 font-bold text-white rounded bg-muted-dark-purple ${
              disabled ? 'opacity-50 cursor-not-allowed' : ''
            }`}
            onClick={handleAppsButton}
            disabled={disabled}
          >
            {title === 'Addable' ? 'Add' : 'Remove'}
          </button>
        </div>
      </div>
      <TextField
        label="Filter"
        variant="standard"
        value={searchTerm}
        onChange={(e): void => setSearchTerm(e.target.value)}
        className="w-full"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Checkbox
                color="default"
                checked={allSelected}
                indeterminate={selectedApps.size > 0 && !allSelected}
                onChange={toggleSelectAllApps}
                data-testid="select-all-apps-checkbox"
                disabled={disabled}
              />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton aria-label="clear filter" size="small" edge="end" onClick={handleClearSearchTerm}>
                <Clear />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      {filteredApps && filteredApps.length > 0 ? (
        <div className="border border-gray-200 rounded-md h-[300px]">
          <AutoSizer>
            {({ height, width }: { height: number; width: number }): JSX.Element => (
              <FixedSizeList height={height} width={width} itemSize={32} itemCount={filteredApps.length}>
                {AppRow}
              </FixedSizeList>
            )}
          </AutoSizer>
        </div>
      ) : (
        <div className="p-4 text-center text-gray-500 rounded-md bg-gray-50">
          {title === 'Addable' ? 'No addable apps' : 'No selected apps'}
        </div>
      )}
    </div>
  );
};

export default CampaignsAppList;
